import React, { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Plus, Edit2, Trash2, ChevronDown, Check, Search } from "lucide-react";
import { WorkforceCache } from "@/services/workforceCache";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import AdicionarNovoModal from "./AdicionarNovoModal";

// Export interfaces for compatibility
export interface WorkforceTeam {
  id?: string;
  name: string;
  notes?: string;
  ownership: "own" | "outsourced";
  members: TeamMember[];
}

export interface Collaborator {
  id: string;
  name: string;
  defaultRole?: string;
}

interface TeamMember {
  id: string;
  type: 'colaborador' | 'profissao';
  colaboradorId?: string;
  profissaoId?: string;
  nome: string;
  funcao: string;
  quantidade: number;
}

interface DropdownItem {
  id: string;
  nome: string;
  type: 'action' | 'profissao' | 'colaborador';
  funcao: string;
  originalId?: string;
}

interface NovaEquipeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave?: (equipe: WorkforceTeam) => void;
  initialTeam?: Partial<WorkforceTeam>;
  collaborators?: Collaborator[];
}

export default function NovaEquipeDialog({
  open,
  onOpenChange,
  onSave,
  initialTeam,
  collaborators: externalCollaborators = [],
}: NovaEquipeDialogProps) {
  const [refreshKey, setRefreshKey] = useState(0);
  const [nomeEquipe, setNomeEquipe] = useState(initialTeam?.name || "");
  const [observacoes, setObservacoes] = useState(initialTeam?.notes || "");
  const [tipoEquipe, setTipoEquipe] = useState<'own' | 'outsourced'>(
    initialTeam?.ownership || 'own'
  );
  const [membrosEquipe, setMembrosEquipe] = useState<TeamMember[]>(
    initialTeam?.members || []
  );
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState("");
  const [quantidade, setQuantidade] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [adicionarNovoOpen, setAdicionarNovoOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);

  // Buscar dados atualizados - combinar com props e cache
  const profissoes = useMemo(() => WorkforceCache.getProfissoes(), [refreshKey]);
  const colaboradoresCache = useMemo(() => WorkforceCache.getColaboradores().filter(c => c.ativo), [refreshKey]);
  
  // Combinar colaboradores do cache com os externos
  const colaboradores = useMemo(() => {
    const combined = [...colaboradoresCache];
    externalCollaborators.forEach(ext => {
      if (!combined.find(c => c.id === ext.id)) {
        combined.push({
          id: ext.id,
          nome: ext.name,
          profissao: ext.defaultRole || '',
          telefone: '',
          email: '',
          endereco: '',
          observacoes: '',
          ativo: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
      }
    });
    return combined;
  }, [colaboradoresCache, externalCollaborators, refreshKey]);

  // Combinar dados para o dropdown
  const allDropdownItems = useMemo((): DropdownItem[] => {
    const items: DropdownItem[] = [
      {
        id: 'add-new',
        nome: 'Adicionar novo colaborador/função',
        type: 'action',
        funcao: ''
      }
    ];

    // Adicionar profissões
    profissoes.forEach(p => {
      items.push({
        id: `profissao-${p.id}`,
        nome: p.nome,
        type: 'profissao',
        funcao: p.nome,
        originalId: p.id
      });
    });

    // Adicionar colaboradores
    colaboradores.forEach(c => {
      items.push({
        id: `colaborador-${c.id}`,
        nome: c.nome,
        type: 'colaborador',
        funcao: c.profissao || '',
        originalId: c.id
      });
    });

    return items;
  }, [profissoes, colaboradores, refreshKey]);

  // Filtrar itens por busca
  const dropdownItems = useMemo(() => {
    if (!searchTerm) return allDropdownItems;
    
    return allDropdownItems.filter(item => 
      item.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.funcao.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [allDropdownItems, searchTerm]);

  const resetForm = () => {
    setNomeEquipe(initialTeam?.name || "");
    setObservacoes(initialTeam?.notes || "");
    setTipoEquipe(initialTeam?.ownership || 'own');
    setMembrosEquipe(initialTeam?.members || []);
    setSelectedValue("");
    setQuantidade(1);
    setEditingMember(null);
    setSearchTerm("");
  };

  const handleAdicionarColaborador = () => {
    if (selectedValue === 'add-new') {
      setAdicionarNovoOpen(true);
      return;
    }

    if (!selectedValue) {
      toast({
        title: "Seleção necessária",
        description: "Selecione um colaborador ou profissão.",
        variant: "destructive"
      });
      return;
    }

    const item = allDropdownItems.find(i => i.id === selectedValue);
    if (!item) return;

    const novoMembro: TeamMember = {
      id: `member_${Date.now()}`,
      type: item.type === 'colaborador' ? 'colaborador' : 'profissao',
      colaboradorId: item.type === 'colaborador' ? item.originalId : undefined,
      profissaoId: item.type === 'profissao' ? item.originalId : undefined,
      nome: item.nome,
      funcao: item.funcao,
      quantidade: quantidade
    };

    setMembrosEquipe(prev => [...prev, novoMembro]);
    setSelectedValue("");
    setQuantidade(1);
    setSearchTerm("");
    setDropdownOpen(false);

    toast({
      title: "Colaborador adicionado",
      description: `${item.nome} foi adicionado à equipe.`,
    });
  };

  const handleEditMember = (member: TeamMember) => {
    setEditingMember(member);
  };

  const handleUpdateMember = (updatedMember: TeamMember) => {
    setMembrosEquipe(prev => 
      prev.map(m => m.id === updatedMember.id ? updatedMember : m)
    );
    setEditingMember(null);
  };

  const handleRemoveMember = (memberId: string) => {
    setMembrosEquipe(prev => prev.filter(m => m.id !== memberId));
    toast({
      title: "Colaborador removido",
      description: "O colaborador foi removido da equipe.",
    });
  };

  const handleSaveEquipe = () => {
    if (!nomeEquipe.trim()) {
      toast({
        title: "Campo obrigatório",
        description: "Nome da equipe é obrigatório.",
        variant: "destructive"
      });
      return;
    }

    const equipe: WorkforceTeam = {
      id: initialTeam?.id || `equipe_${Date.now()}`,
      name: nomeEquipe.trim(),
      notes: observacoes.trim(),
      ownership: tipoEquipe,
      members: membrosEquipe,
    };

    onSave?.(equipe);
    
    toast({
      title: "Equipe salva",
      description: `A equipe "${nomeEquipe}" foi salva com sucesso.`,
    });

    onOpenChange(false);
  };

  const handleAdicionado = () => {
    setRefreshKey(prev => prev + 1);
  };

  React.useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open]);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {initialTeam ? "Editar Equipe de Mão de Obra" : "Nova Equipe de Mão de Obra"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 flex-1 overflow-hidden">
            {/* Campos principais */}
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="sm:col-span-2">
                <Label htmlFor="nome-equipe">Nome da Equipe *</Label>
                <Input
                  id="nome-equipe"
                  value={nomeEquipe}
                  onChange={(e) => setNomeEquipe(e.target.value)}
                  placeholder="Digite o nome da equipe"
                />
              </div>

              <div className="sm:col-span-1">
                <Label>Tipo da Equipe</Label>
                <div className="flex gap-2 mt-1">
                  <Button
                    type="button"
                    variant={tipoEquipe === 'own' ? 'secondary' : 'outline'}
                    size="sm"
                    onClick={() => setTipoEquipe('own')}
                    className="flex-1"
                  >
                    Própria
                  </Button>
                  <Button
                    type="button"
                    variant={tipoEquipe === 'outsourced' ? 'secondary' : 'outline'}
                    size="sm"
                    onClick={() => setTipoEquipe('outsourced')}
                    className="flex-1"
                  >
                    Terceirizada
                  </Button>
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="observacoes">Observação</Label>
              <Textarea
                id="observacoes"
                rows={2}
                value={observacoes}
                onChange={(e) => setObservacoes(e.target.value)}
                placeholder="Observações sobre a equipe (opcional)"
              />
            </div>

            {/* Adicionar colaboradores */}
            <div className="space-y-3">
              <Label>Adicionar Colaboradores à equipe</Label>
              
              <div className="flex gap-2">
                <div className="flex-1">
                  <Select
                    value={selectedValue}
                    onValueChange={(value) => setSelectedValue(value)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Buscar profissões e colaboradores..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="add-new" className="text-primary">
                        Adicionar novo colaborador/função
                      </SelectItem>
                      {dropdownItems.slice(1).map((item) => (
                        <SelectItem key={item.id} value={item.id}>
                          <div className="flex items-center justify-between w-full">
                            {item.nome}
                            {item.funcao && (
                              <Badge variant="outline" className="ml-2">
                                {item.funcao}
                              </Badge>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="w-20">
                  <Input
                    type="number"
                    min={1}
                    value={quantidade}
                    onChange={(e) => setQuantidade(Number(e.target.value) || 1)}
                    placeholder="Qtd"
                  />
                </div>

                <Button onClick={handleAdicionarColaborador}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Lista dinâmica de colaboradores */}
            <div className="space-y-3 flex-1 overflow-hidden">
              <div className="flex items-center justify-between">
                <Label>Colaboradores da equipe ({membrosEquipe.length})</Label>
              </div>

              <ScrollArea className="h-full max-h-64 pr-4">
                {membrosEquipe.length === 0 ? (
                  <div className="border border-dashed rounded-lg p-6 text-center text-muted-foreground">
                    Nenhum colaborador adicionado à equipe
                  </div>
                ) : (
                  <div className="space-y-2">
                    {membrosEquipe.map((membro) => (
                      <Card key={membro.id} className="p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{membro.nome}</span>
                              {membro.funcao && (
                                <Badge variant="secondary" className="text-xs">
                                  {membro.funcao}
                                </Badge>
                              )}
                              <Badge variant="outline" className="text-xs">
                                Qtd: {membro.quantidade}
                              </Badge>
                            </div>
                          </div>
                          
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditMember(membro)}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveMember(membro.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>
          </div>

          <DialogFooter className="shrink-0">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveEquipe}>
              Salvar Equipe
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de edição de membro */}
      {editingMember && (
        <Dialog open={!!editingMember} onOpenChange={() => setEditingMember(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Editar Colaborador</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <Label>Nome</Label>
                <Input
                  value={editingMember.type === 'profissao' ? '' : editingMember.nome}
                  onChange={(e) => setEditingMember(prev => 
                    prev ? { ...prev, nome: e.target.value } : null
                  )}
                  disabled={editingMember.type === 'profissao'}
                  placeholder={editingMember.type === 'profissao' ? 'Profissão não possui nome individual' : 'Nome do colaborador'}
                />
              </div>
              
              <div>
                <Label>Função</Label>
                <Input
                  value={editingMember.funcao}
                  onChange={(e) => setEditingMember(prev => 
                    prev ? { ...prev, funcao: e.target.value } : null
                  )}
                  disabled={editingMember.type === 'profissao'}
                />
              </div>
              
              <div>
                <Label>Quantidade</Label>
                <Input
                  type="number"
                  min={1}
                  value={editingMember.quantidade}
                  onChange={(e) => setEditingMember(prev => 
                    prev ? { ...prev, quantidade: Number(e.target.value) || 1 } : null
                  )}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingMember(null)}>
                Cancelar
              </Button>
              <Button onClick={() => editingMember && handleUpdateMember(editingMember)}>
                Salvar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Modal Adicionar Novo */}
      <AdicionarNovoModal
        open={adicionarNovoOpen}
        onOpenChange={setAdicionarNovoOpen}
        onAdicionado={handleAdicionado}
      />
    </>
  );
}